/**
 * Tutor edit form.
 * Loads full state + assignments, hydrates the form, then saves the profile and the criminal-record decision.
 */
import * as React from 'react';
import { useQuery, useQueries } from '@tanstack/react-query';
import {
  useUpdateInPersonTutorProfile,
  useGetInPersonTutorFullState,
  useEligibleEstablishments,
  useInPersonTutorAssignments,
  useUpdateCriminalRecordDecision,
} from '@/hooks/useUsers';
import { useAuthStore } from '@/stores/authStore';
import { usePageActions } from '@/hooks/usePageActions';
import { CollapseReplaceForm } from '@/components/common/CollapseReplaceForm';
import { getSubjects, getSubject } from '@/services/subjects.service';
import { sortByInpersonSubjectLabel } from '@/utils/inperson-subject-order-utils';
import { useProgramsForMode } from '@/hooks/useProgramsForMode';
import {
  areContactsIdentical,
  isPrimaryContactDirty,
  isPrimaryContactFormValid,
  isPrimaryContactSubmitBlocked,
  readPhoneSetupLink,
  toPrimaryContactMode,
  useDefaultPhoneCountry,
} from '@/utils/user-contact-utils';
import { ContactChangeModal } from '@/components/common/modals/ContactChangeModal';
import { useContactChangeOnSubmit } from '@/hooks/useContactChangeOnSubmit';
import { useFormValidation } from '@/hooks/useFormValidation';
import { useUnitGetNotFound } from '@/hooks/useUnitGetNotFound';
import { tutorInPersonEditValidationSchema } from '@/utils/validation-schemas-utils';
import { useContactExistsCheck, resolveContactCheckType } from '@/hooks/useContactExistsCheck';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import type { TutorRecord } from '@/types/inperson/tutor.types';
import {
  applyInPersonTutorFieldUpdate,
  buildEditInitialData,
  buildTutorProfileDto,
  checkEngagementEndDateError,
  computeInPersonAllowedSlotsFromPrograms,
  defaultUpload,
  deriveAssignmentCheckboxStateFromAffectations,
  filterOptionsBySearchQuery,
  isCriminalRecordAlreadyValidated,
  mapEstablishmentOptions,
  mergePendingUploads,
  normalizeTutorSubjectIds,
  setNestedPath,
  syncCriminalRecordOnEdit,
  validateInPersonProSituationFields,
} from './tutor-form-utils';
import { hasPendingUploadFiles, uploadPendingFiles } from '@/utils/pending-upload-utils';
import { preserveClearedMeetingSessionId } from '@/utils/meeting-selector-utils';
import { isCriminalRecordMetadataComplete } from '@/utils/criminal-record-utils';
import { buildPendingDocumentFilePatch } from '@/utils/pending-document-link-utils';
import { TutorPersonalAccountSection } from './tutor-form-builder/TutorPersonalAccountSection';
import { TutorSupportSection } from './tutor-form-builder/TutorSupportSection';
import { TutorInformationMeetingSection } from './tutor-form-builder/TutorInformationMeetingSection';
import { TutorOfficialDocumentsSection } from './tutor-form-builder/TutorOfficialDocumentsSection';
import { TutorCertificationSection } from './tutor-form-builder/TutorCertificationSection';
import { TutorContractSection } from './tutor-form-builder/TutorContractSection';
import { TutorPermissionsSection } from './tutor-form-builder/TutorPermissionsSection';

/** Props for {@link EditTutorView}. */
interface EditTutorViewProps {
  tutorUserId: string;
  onClose: () => void;
  onSuccess?: () => void;
}

/** Tutor edit form. */
export function EditTutorView({ tutorUserId, onClose, onSuccess }: Readonly<EditTutorViewProps>) {
  const { user } = useAuthStore();
  const companyId = user?.companyId ?? '';
  const defaultCountry = useDefaultPhoneCountry();
  const tutorPageActions = usePageActions('tutors');
  const validation = useFormValidation(tutorInPersonEditValidationSchema);

  const {
    data: fullState,
    isLoading: isLoadingFullState,
    isError: isFullStateError,
    error: fullStateError,
  } = useGetInPersonTutorFullState(tutorUserId, companyId, {
    enabled: !!tutorUserId && !!companyId,
  });
  useUnitGetNotFound({
    isError: isFullStateError,
    error: fullStateError,
    catalogKey: 'tutorNotFound',
    onClose,
    entityId: tutorUserId,
  });

  const { data: subjects = [] } = useQuery({
    queryKey: ['subjects', 'presentiel'],
    queryFn: async () =>
      (await getSubjects({ page: 1, pageSize: 20, displayOrder: 'presentiel' })).data,
  });

  const { data: programsResponse } = useProgramsForMode();
  const catalogPrograms = programsResponse?.data;
  const programsData = catalogPrograms ?? [];

  /** Subject ids already stored on the profile (may include ids missing from the first page of {@link getSubjects}). */
  const preSelectedSubjectIds = React.useMemo(() => {
    if (!fullState) return [] as string[];
    const profile = (fullState as any)?.record?.user?.userProfiles?.[0];
    return Array.isArray(profile?.subjects) ? (profile.subjects as string[]) : [];
  }, [fullState]);

  /** Ids from the first {@link getSubjects} page — used to spot profile ids that still need a label fetch. */
  const knownSubjectIds = React.useMemo(
    () => new Set((subjects as any[]).map((s: any) => s.id as string)),
    [subjects],
  );

  /** Profile subject ids not in the paginated list — fetched one-by-one so the {@link MultiSelect} still shows a label. */
  const missingSubjectIds = React.useMemo(
    () => preSelectedSubjectIds.filter((id) => !knownSubjectIds.has(id)),
    [preSelectedSubjectIds, knownSubjectIds],
  );

  const missingSubjectResults = useQueries({
    queries: missingSubjectIds.map((id) => ({
      queryKey: ['subject', id],
      queryFn: () => getSubject(id),
    })),
  });

  /** Paginated subjects plus any extra ids fetched for the current profile. */
  const subjectOptions = React.useMemo(() => {
    const baseOptions = (subjects as any[]).map((s: any) => ({
      value: s.id as string,
      label: s.label as string,
    }));
    const known = new Set(baseOptions.map((option) => option.value));
    const extraOptions = missingSubjectResults
      .filter((q) => q.data != null)
      .map((q) => ({
        value: (q.data as any).id as string,
        label: (q.data as any).label as string,
      }))
      .filter((option) => !known.has(option.value));
    return sortByInpersonSubjectLabel([...baseOptions, ...extraOptions]);
  }, [subjects, missingSubjectResults]);

  /** Hydrated form shape from GET full-state (empty until the query resolves). */
  const initialData = React.useMemo<Record<string, any>>(() => {
    if (!fullState) return {};
    return buildEditInitialData(fullState, defaultCountry, catalogPrograms);
  }, [fullState, defaultCountry, catalogPrograms]);

  const [formData, setFormData] = React.useState<Record<string, any>>(initialData);
  const [isHydrated, setIsHydrated] = React.useState(false);
  const [pendingFiles, setPendingFiles] = React.useState<Record<string, File | null>>({});
  const [isUploading, setIsUploading] = React.useState(false);

  const primaryContactValue = formData.contact || '';
  const secondaryContactValue = formData.secondaryContact || '';
  const secondaryContactType: 'email' | 'phone' =
    formData.contactType === 'email' ? 'phone' : 'email';

  const { isChecking: isPrimaryChecking, exists: primaryContactExists } = useContactExistsCheck({
    value: primaryContactValue,
    type: resolveContactCheckType(formData.contactType || 'email'),
    excludeUserId: tutorUserId,
    skipHydratedValue: true,
  });

  const { isChecking: isSecondaryChecking, exists: contactExists } = useContactExistsCheck({
    value: secondaryContactValue,
    type: resolveContactCheckType(secondaryContactType),
    excludeUserId: tutorUserId,
    skipHydratedValue: true,
  });

  const contactsAreIdentical = areContactsIdentical(primaryContactValue, secondaryContactValue);
  const serverUser = (fullState as { record?: TutorRecord } | undefined)?.record?.user;
  const contactKind = toPrimaryContactMode(formData.contactType);
  const isPrimaryDirty = isPrimaryContactDirty({
    mode: contactKind,
    currentValue: primaryContactValue,
    storedEmail: serverUser?.email,
    storedPhone: serverUser?.phone,
    serverKind: serverUser?.primaryContactKind,
    defaultCountry,
  });

  /** Close the edit surface after a credential modal or a clean PATCH. */
  function handleContactChangeFinished(): void {
    onSuccess?.();
  }

  const contactChange = useContactChangeOnSubmit({
    isDirty: isPrimaryDirty,
    kind: contactKind,
    ownProfile: false,
    closeForm: handleContactChangeFinished,
  });

  const formDataRef = React.useRef(formData);
  formDataRef.current = formData;

  React.useEffect(() => {
    if (Object.keys(initialData).length === 0) {
      setIsHydrated(false);
      return;
    }
    const next = preserveClearedMeetingSessionId(formDataRef.current, initialData);
    setFormData(next);
    validation.validateInitialFormData(next);
    setIsHydrated(true);
  }, [initialData]);

  /** Legacy rows stored subject labels; rewrite them to UUIDs once options are loaded. */
  React.useEffect(() => {
    const normalized = normalizeTutorSubjectIds(
      formData.subjects as string[] | undefined,
      subjectOptions,
    );
    if (!normalized) return;
    setFormData((prev) => ({ ...prev, subjects: normalized }));
  }, [subjectOptions]);

  const selectedDepartments = (formData.departments ?? []) as string[];
  const { data: eligibleEstablishments = [], isLoading: isLoadingEstablishments } =
    useEligibleEstablishments(selectedDepartments, companyId, {
      enabled: selectedDepartments.length > 0,
    });

  /** Eligible establishments for the selected departments ({@link Select} options). */
  const allEstablishmentOptions = React.useMemo(
    () => mapEstablishmentOptions(eligibleEstablishments as Array<{ id?: string; name?: string }>),
    [eligibleEstablishments],
  );

  const [establishmentSearch, setEstablishmentSearch] = React.useState('');
  /** Client-side filter of {@link allEstablishmentOptions} by the search box. */
  const establishmentOptions = React.useMemo(
    () => filterOptionsBySearchQuery(allEstablishmentOptions, establishmentSearch),
    [allEstablishmentOptions, establishmentSearch],
  );

  /** Availability slots allowed by the selected programs (`undefined` = no restriction). */
  const allowedSlots = React.useMemo(
    () => computeInPersonAllowedSlotsFromPrograms(programsData as any[], formData.programs),
    [formData.programs, programsData],
  );

  const updateMutation = useUpdateInPersonTutorProfile();
  const criminalRecordDecisionMutation = useUpdateCriminalRecordDecision();

  const { data: affectationsData = [] } = useInPersonTutorAssignments(tutorUserId, companyId);

  /** Mirror assignment API statuses onto the validated / preValidated checkboxes. */
  React.useEffect(() => {
    const state = deriveAssignmentCheckboxStateFromAffectations(affectationsData as any[]);
    if (!state) return;
    setFormData((prev) => ({
      ...prev,
      validated: state.validated,
      preValidated: state.preValidated,
    }));
  }, [affectationsData]);

  /** Whether the criminal record was already validated — skip a second validate call on save. */
  const wasValidated = isCriminalRecordAlreadyValidated(initialData.criminalRecord);

  /** Set one top-level field. Changing departments clears selected establishments. */
  const set = (field: string, value: any) => {
    setFormData((prev) => {
      const next = applyInPersonTutorFieldUpdate(prev, field, value);
      validation.validateField(field, value, { ...next, _contactType: next.contactType });
      validation.markAsTouched(field);
      validation.markAsDirty(field);
      return next;
    });
  };

  /** Patch several fields at once (supports dotted paths). Validates after the state flush. */
  const setBatch = (patch: Record<string, any>) => {
    setFormData((prev) => {
      let next = { ...prev };
      Object.entries(patch).forEach(([key, value]) => {
        if (key.includes('.')) {
          next = setNestedPath(next, key, value);
        } else {
          next[key] = value;
        }
      });
      formDataRef.current = next;
      return next;
    });
    setTimeout(() => {
      Object.entries(patch).forEach(([k, v]) => {
        validation.validateField(k, v, formDataRef.current);
        validation.markAsTouched(k);
        validation.markAsDirty(k);
      });
    }, 0);
  };

  /** Blocking error when engagement end is before start. */
  const engagementEndDateError = React.useMemo(
    () =>
      checkEngagementEndDateError(
        formData.engagementStartDate as string | undefined,
        formData.engagementEndDate as string | undefined,
      ),
    [formData.engagementStartDate, formData.engagementEndDate],
  );

  /** Merge one key into `formData.criminalRecord`. */
  const setCriminalRecord = (key: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      criminalRecord: { ...prev.criminalRecord, [key]: value },
    }));
  };

  const [submitAttempted, setSubmitAttempted] = React.useState(false);

  /** Student / high-school / other require establishment and/or valued engagement. */
  const proSituationError = React.useMemo(
    () => validateInPersonProSituationFields(formData),
    [
      formData.professionalSituation,
      formData.proSituationEstablishmentId,
      formData.proSituationAssociativeEngagement,
    ],
  );

  /** Store the file for submit upload and write a pending link so required docs validate. */
  const handleFileChange = (key: string, file: File | null) => {
    setPendingFiles((prev) => ({ ...prev, [key]: file }));
    const patch = buildPendingDocumentFilePatch(key, file);
    if (patch) setBatch(patch);
  };

  /** Validate, upload pending files, PATCH profile, then sync the criminal-record decision. */
  const handleSubmit = async () => {
    if (
      isPrimaryContactSubmitBlocked(primaryContactExists, isPrimaryChecking, contactsAreIdentical)
    )
      return;
    setSubmitAttempted(true);
    const isValid = validation.validateForm({
      ...formData,
      contact: primaryContactValue,
      _contactType: formData.contactType,
      _primaryContactExists: primaryContactExists,
      _contactExists: contactExists,
    });
    if (!isValid) {
      toast.error(getErrorMessage('requiredFields'));
      return;
    }
    if (proSituationError) {
      toast.error(getErrorMessage(proSituationError));
      return;
    }
    if (engagementEndDateError) return;

    if (hasPendingUploadFiles(pendingFiles)) {
      setIsUploading(true);
    }
    let uploadedUrls: Record<string, string> = {};
    try {
      uploadedUrls = await uploadPendingFiles(pendingFiles, defaultUpload);
    } catch (err) {
      toast.error(`Erreur lors de l'upload : ${getErrorMessage(err)}`);
      setIsUploading(false);
      return;
    }

    const enrichedData = mergePendingUploads(formData, uploadedUrls, false);

    const dto = buildTutorProfileDto('edit', enrichedData, defaultCountry);

    const run = async () => {
      let result: unknown;
      try {
        result = await updateMutation.mutateAsync({ userId: tutorUserId, dto, companyId });
      } catch (error: unknown) {
        toast.error(getErrorMessage(error));
        contactChange.onMutationSettled({ ok: false });
        return;
      }
      try {
        await syncCriminalRecordOnEdit(
          tutorUserId,
          formData.criminalRecord?.markValidated === true,
          wasValidated,
          companyId,
          criminalRecordDecisionMutation.mutateAsync,
        );
      } catch (error: unknown) {
        toast.error(getErrorMessage(error));
      }
      if (contactChange.isIntercepting) {
        contactChange.onMutationSettled({ ok: true, link: readPhoneSetupLink(result) });
        return;
      }
      toast.success('Tuteur mis à jour avec succès');
      handleContactChangeFinished();
    };
    contactChange.requestSubmit(() => {
      void run();
    });
    setIsUploading(false);
  };

  /** Nested criminal-record slice (empty object until hydrated). */
  const criminalRecord = formData.criminalRecord ?? {};
  /** Number, issue date, and authority are all present — unlock validate checkbox. */
  const allCriminalRecordFilled = isCriminalRecordMetadataComplete(criminalRecord);
  /** True until GET tutor hydrates the form. */
  const isFormLoading = isLoadingFullState || !isHydrated;

  return (
    <>
      <CollapseReplaceForm
        formType="edit"
        title="Modifier le tuteur"
        subtitle="Modifiez les informations du tuteur"
        onClose={onClose}
        onSubmit={handleSubmit}
        isSubmitting={isUploading || updateMutation.isPending || contactChange.modal.open}
        isValid={
          isPrimaryContactFormValid({
            primaryExists: primaryContactExists,
            isPrimaryChecking,
            isSecondaryChecking,
            identical: contactsAreIdentical,
            formValid: validation.isFormValid(),
          }) &&
          !engagementEndDateError &&
          !proSituationError
        }
        submitLabel="Enregistrer"
        isLoading={isFormLoading}
      >
        <div className="space-y-6">
          <TutorPersonalAccountSection
            mode="edit"
            formData={formData}
            fullState={fullState}
            defaultCountry={defaultCountry}
            set={set}
            submitAttempted={submitAttempted}
            userId={tutorUserId}
            emailVerified={
              (fullState as { record?: TutorRecord } | undefined)?.record?.user?.emailVerified
            }
            contactExists={contactExists}
            primaryContactExists={primaryContactExists}
            contactsAreIdentical={contactsAreIdentical}
            validation={validation}
          />
          <TutorSupportSection
            mode="edit"
            formData={formData}
            fullState={fullState}
            programsData={programsData}
            subjectOptions={subjectOptions}
            establishmentOptions={establishmentOptions}
            selectedDepartments={selectedDepartments}
            isLoadingEstablishments={isLoadingEstablishments}
            allowedSlots={allowedSlots}
            tutorPageActions={tutorPageActions}
            set={set}
            setEstablishmentSearch={setEstablishmentSearch}
            engagementEndDateError={engagementEndDateError}
          />
          <TutorOfficialDocumentsSection
            mode="edit"
            tutorUserId={tutorUserId}
            criminalRecord={criminalRecord}
            allCriminalRecordFilled={allCriminalRecordFilled}
            set={set}
            setCriminalRecord={setCriminalRecord}
            setBatch={setBatch}
          />
          <TutorInformationMeetingSection
            mode="edit"
            formData={formData}
            tutorUserId={tutorUserId}
            companyId={companyId}
            user={user}
            set={set}
            getFieldError={validation.getFieldError}
          />
          <TutorCertificationSection
            formData={formData}
            set={set}
            onFileSelect={handleFileChange}
          />
          <TutorContractSection
            formData={formData}
            set={set}
            setBatch={setBatch}
            onFileSelect={handleFileChange}
          />
          <TutorPermissionsSection formData={formData} set={set} />
        </div>
      </CollapseReplaceForm>
      <ContactChangeModal
        open={contactChange.modal.open}
        kind={contactChange.modal.kind}
        ownProfile={contactChange.modal.ownProfile}
        phase={contactChange.modal.phase}
        phoneSetupLink={contactChange.modal.phoneSetupLink}
        onClose={contactChange.cancel}
        onConfirm={contactChange.confirm}
        onCopyLink={contactChange.onCopyLink}
      />
    </>
  );
}
